// Sidebar.jsx (обновленный фрагмент)
import React, { useEffect, useMemo, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ClipboardList,
  BarChart3,
  Stethoscope,
  Wrench,
  ShieldCheck,
  Sparkles,
  Package,
  Truck,
  MapPin,
  FileText,
  CheckCircle,
  History,
  Clock,
  Boxes,
  X
} from 'lucide-react';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { useAuth } from '../../context/AuthContext';
import NeuralSpinner from '../NeuralSpinner';
import styles from './Sidebar.module.css';

const Sidebar = ({ isMobile = false, isOpen = false, onClose }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const sidebarRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      // Устанавливаем ширину сайдбара как CSS-переменную
      const sidebar = document.querySelector(`.${styles.sidebar}`);
      if (sidebar) {
        const sidebarWidth = sidebar.offsetWidth;
        document.documentElement.style.setProperty('--sidebar-width', `${sidebarWidth}px`);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Вызываем сразу для начальной установки
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Обновляем позицию градиента при изменении активной кнопки
  useEffect(() => {
    const updateGradientPosition = () => {
      if (!sidebarRef.current) return;

      const activeLink = sidebarRef.current.querySelector(`.${styles.active}`);
      if (activeLink) {
        const rect = activeLink.getBoundingClientRect();
        const sidebarRect = sidebarRef.current.getBoundingClientRect();
        const relativeTop = rect.top - sidebarRect.top;
        const centerY = relativeTop + rect.height / 2;
        const percentage = (centerY / sidebarRect.height) * 100;

        sidebarRef.current.style.setProperty('--gradient-position', `${percentage}%`);
      }
    };

    setTimeout(updateGradientPosition, 100);
    window.addEventListener('resize', updateGradientPosition);

    return () => window.removeEventListener('resize', updateGradientPosition);
  }, [location.pathname, user]);

  const handleMouseMove = (e) => {
    if (!sidebarRef.current) return;

    const rect = sidebarRef.current.getBoundingClientRect();
    const relativeY = e.clientY - rect.top;
    const percentage = (relativeY / rect.height) * 100;

    sidebarRef.current.style.setProperty('--gradient-position', `${percentage}%`);
  };

  const handleMouseLeave = () => {
    if (!sidebarRef.current) return;

    // Возвращаем градиент к активной кнопке
    const activeLink = sidebarRef.current.querySelector(`.${styles.active}`);
    if (activeLink) {
      const rect = activeLink.getBoundingClientRect();
      const sidebarRect = sidebarRef.current.getBoundingClientRect();
      const relativeTop = rect.top - sidebarRect.top;
      const centerY = relativeTop + rect.height / 2;
      const percentage = (centerY / sidebarRect.height) * 100;

      sidebarRef.current.style.setProperty('--gradient-position', `${percentage}%`);
    }
  };

  const repairRequests = useAutoTranslate("Заявки на ремонт");
  const diagnostics = useAutoTranslate("Диагностика");
  const repair = useAutoTranslate("Ремонт");
  const waitingParts = useAutoTranslate("Ожидание ЗИП");
  const qualityControl = useAutoTranslate("Контроль качества");
  const cleaning = useAutoTranslate("Клининг");
  const packaging = useAutoTranslate("Упаковка");
  const shipping = useAutoTranslate("Отправка");
  const tracking = useAutoTranslate("Отслеживание");
  const baskets = useAutoTranslate("Корзины");
  const reportConstructor = useAutoTranslate("Конструктор отчётов");
  const approvals = useAutoTranslate("Согласование");
  const history = useAutoTranslate("История");

  const navItems = useMemo(() => ([
    {
      path: "/",
      permission: "crm.page.dashboard",
      icon: <BarChart3 className={styles.icon} />,
      text: "Моя статистика",
      roles: ["admin", "приёмщик", "Специалист по диагностике", "Инженер по ремонту", "Специалист по QA", "Специалист по клинингу", "Специалист по упаковке", "Специалист по логистике", "Ассистент"]
    },
    {
      path: "/repair-requests",
      permission: "crm.page.repair_requests",
      icon: <ClipboardList className={styles.icon} />,
      text: repairRequests,
      roles: ["admin", "приёмщик", "Специалист по диагностике", "Инженер по ремонту", "Специалист по QA", "Специалист по клинингу", "Специалист по упаковке", "Специалист по логистике", "Ассистент", "Гость"]
    },
    {
      path: "/diagnostics",
      permission: "crm.page.diagnostics",
      icon: <Stethoscope className={styles.icon} />,
      text: diagnostics,
      roles: ["admin", "Специалист по диагностике"]
    },
    {
      path: "/repair",
      permission: "crm.page.repair",
      icon: <Wrench className={styles.icon} />,
      text: repair,
      roles: ["admin", "Инженер по ремонту"]
    },
    {
      path: "/waiting-parts",
      permission: "crm.page.waiting_parts",
      icon: <Clock className={styles.icon} />,
      text: waitingParts,
      roles: ["admin", "Специалист по диагностике", "Инженер по ремонту"]
    },
    {
      path: "/quality-control",
      permission: "crm.page.quality_control",
      icon: <ShieldCheck className={styles.icon} />,
      text: qualityControl,
      roles: ["admin", "Специалист по QA"]
    },
    {
      path: "/cleaning",
      permission: "crm.page.cleaning",
      icon: <Sparkles className={styles.icon} />,
      text: cleaning,
      roles: ["admin", "Специалист по клинингу"]
    },
    {
      path: "/packaging",
      permission: "crm.page.packaging",
      icon: <Package className={styles.icon} />,
      text: packaging,
      roles: ["admin", "приёмщик", "Специалист по упаковке"]
    },
    {
      path: "/shipping",
      permission: "crm.page.shipping",
      icon: <Truck className={styles.icon} />,
      text: shipping,
      roles: ["admin", "Специалист по логистике"]
    },
    {
      path: "/tracking",
      permission: "crm.page.tracking",
      icon: <MapPin className={styles.icon} />,
      text: tracking,
      roles: ["admin", "приёмщик", "Специалист по диагностике", "Инженер по ремонту", "Специалист по QA", "Специалист по клинингу", "Специалист по упаковке", "Специалист по логистике", "Ассистент", "Гость"]
    },
    {
      path: "/baskets",
      permission: "crm.page.baskets",
      icon: <Boxes className={styles.icon} />,
      text: baskets,
      roles: ["admin", "приёмщик", "Специалист по диагностике", "Инженер по ремонту", "Специалист по QA", "Специалист по клинингу", "Специалист по упаковке", "Специалист по логистике", "Ассистент"]
    },
    {
      path: "/approvals",
      permission: "crm.page.approvals",
      icon: <CheckCircle className={styles.icon} />,
      text: approvals,
      roles: ["admin", "Ассистент"]
    },
    {
      path: "/reports",
      permission: "crm.page.reports",
      icon: <FileText className={styles.icon} />,
      text: reportConstructor,
      roles: ["admin"]
    },
    {
      path: "/history",
      permission: "crm.page.history",
      icon: <History className={styles.icon} />,
      text: history,
      roles: ["admin", "приёмщик", "Специалист по диагностике", "Инженер по ремонту", "Специалист по QA", "Специалист по клинингу", "Специалист по упаковке", "Специалист по логистике", "Ассистент", "Гость"]
    }
  ]), [approvals, baskets, cleaning, history, packaging, repair, repairRequests, reportConstructor, shipping, tracking, diagnostics, qualityControl, waitingParts]);

  if (loading) {
    console.log('Sidebar: Ожидание загрузки пользователя, loading:', loading);
    const sidebarClasses = [
      styles.sidebar,
      isMobile ? styles.mobileSidebar : '',
      isMobile && isOpen ? styles.open : '',
      styles.loadingState,
    ].filter(Boolean).join(' ');
    return (
      <>
        <aside ref={sidebarRef} className={sidebarClasses}>
          {isMobile && (
            <div className={styles.sidebarHeader}>
              <button className={styles.closeButton} onClick={onClose}>
                <X size={20} />
              </button>
            </div>
          )}
          <div className={styles.loader}>
            <NeuralSpinner />
            <p className={styles.loaderText}>Загрузка меню...</p>
          </div>
        </aside>
        {isMobile && isOpen && (
          <div className={styles.overlay} onClick={onClose} />
        )}
      </>
    );
  }

  if (!user) {
    console.log('Sidebar: Пользователь не загружен, user:', user);
    return null; // Do not render if no user
  }

  const roleNames = (user?.roles || []).map(role => role.name);
  const permissionKeys = new Set(user?.permissions || []);
  const hasConfiguredPermissions = Array.isArray(user?.permissions);
  console.log('Sidebar: Рендеринг с ролями:', roleNames);

  const filteredNavItems = navItems.filter(item => (
    hasConfiguredPermissions
      ? permissionKeys.has(item.permission) || permissionKeys.has('crm.admin.override')
      : item.roles.some(role => roleNames.includes(role))
  ));

  return (
    <>
      <aside
        ref={sidebarRef}
        className={`
          ${styles.sidebar}
          ${isMobile ? styles.mobileSidebar : ''}
          ${isMobile && isOpen ? styles.open : ''}
        `}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {isMobile && (
          <div className={styles.sidebarHeader}>
            <button className={styles.closeButton} onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        )}
        <nav>
          <ul className={styles.navList}>
            {filteredNavItems.map((item) => (
              <li key={item.path}>
                <NavLink 
                  to={item.path} 
                  end={item.path === "/repair-requests"}
                  className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
                  onClick={() => isMobile && onClose?.()}
                >
                  {item.icon}
                  <span>{item.text}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
      {isMobile && isOpen && (
        <div className={styles.overlay} onClick={onClose} />
      )}
    </>
  );
};

export default Sidebar;
